import {useEffect} from 'react';
import {fetchIdentificationInfo} from '@rocket-ui/lk-api';
import {
	extractRegistrationBackendFlags,
	mapIdentificationInfoToPersonalInfo,
	readSavedSnilsDigits,
	normalizeRegistrationOksmCode,
	useRegistrationCountries,
	type PersonalInfoStepValues,
	type RegistrationCountry,
} from 'features/registration';
import {EMPTY_PERSONAL_INFO} from './constants';
import type {RegistrationWizardState} from './useRegistrationWizardState';

const isPersonalInfoUntouched = (values: PersonalInfoStepValues): boolean => {
	return (Object.keys(EMPTY_PERSONAL_INFO) as (keyof PersonalInfoStepValues)[]).every(
		(key) => values[key] === EMPTY_PERSONAL_INFO[key],
	);
};

const isEmptyValue = (value: unknown): boolean => value === null || value === '';

/** Не перетирает то, что пользователь уже ввёл; пол берём с бэка, только если форму ещё не трогали. */
const mergePrefill = (
	current: PersonalInfoStepValues,
	prefill: Partial<PersonalInfoStepValues>,
): PersonalInfoStepValues => {
	if (isPersonalInfoUntouched(current)) {
		return {...current, ...prefill};
	}

	const next = {...current};

	for (const key of Object.keys(prefill) as (keyof PersonalInfoStepValues)[]) {
		if (key !== 'gender' && isEmptyValue(next[key])) {
			(next as Record<string, unknown>)[key] = prefill[key];
		}
	}

	return next;
};

const findCountry = (
	countries: RegistrationCountry[],
	oksmCode: string | null,
): RegistrationCountry | undefined => {
	if (!oksmCode) {
		return undefined;
	}

	return (
		countries.find((country) => country.oksmCode === oksmCode) ??
		countries.find(
			(country) => normalizeRegistrationOksmCode(country.oksmCode) === normalizeRegistrationOksmCode(oksmCode),
		)
	);
};

/** Шаг «Давайте познакомимся»: подставляем ранее сохранённые данные из GET /identification/info. */
export const usePersonalInfoPrefill = (state: RegistrationWizardState) => {
	const {personalInfo, setBackendFlags, setHasSnilsSaved, setPersonalInfo, setSnilsStep} = state;
	const {countries} = useRegistrationCountries();

	useEffect(() => {
		let cancelled = false;

		void fetchIdentificationInfo()
			.then(({data}) => {
				if (cancelled) {
					return;
				}

				setBackendFlags(extractRegistrationBackendFlags(data));

				const savedSnils = readSavedSnilsDigits(data?.info);

				if (savedSnils) {
					setHasSnilsSaved(true);
					setSnilsStep({snils: savedSnils});
				}

				const prefill = mapIdentificationInfoToPersonalInfo(data);

				if (Object.keys(prefill).length > 0) {
					setPersonalInfo((current) => mergePrefill(current, prefill));
				}
			})
			.catch(() => {});

		return () => {
			cancelled = true;
		};
	}, []);

	useEffect(() => {
		const needsCitizenshipName = personalInfo.citizenshipOksmCode && !personalInfo.citizenshipCountryName;
		const needsResidenceName = personalInfo.residenceOksmCode && !personalInfo.residenceCountryName;

		if ((!needsCitizenshipName && !needsResidenceName) || countries.length === 0) {
			return;
		}

		const citizenship = needsCitizenshipName ? findCountry(countries, personalInfo.citizenshipOksmCode) : undefined;
		const residence = needsResidenceName ? findCountry(countries, personalInfo.residenceOksmCode) : undefined;

		if (!citizenship && !residence) {
			return;
		}

		setPersonalInfo((current) => ({
			...current,
			...(citizenship ? {citizenshipCountryName: citizenship.name} : {}),
			...(residence ? {residenceCountryName: residence.name} : {}),
		}));
	}, [
		countries,
		personalInfo.citizenshipCountryName,
		personalInfo.citizenshipOksmCode,
		personalInfo.residenceCountryName,
		personalInfo.residenceOksmCode,
	]);
};
